/* iter150AG Part 26 — AI rotation-policy recommender card.
 *
 * Drops into the Settings tab below the rotation-policy radios.
 * Calls Claude Sonnet 4.5 via the backend on demand, shows the
 * recommendation with confidence, comparison table, and a single
 * "Apply" button that patches the group with the suggested settings.
 *
 * Cached server-side for 24h keyed by a hash of the group inputs.
 */
import { useEffect, useState } from "react";
import { Sparkle, CircleNotch, CheckCircle, Warning,
         ArrowsCounterClockwise, Lightning, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  seqPolicyRecommendation, seqPolicyRecommendationLatest, seqPatchGroup,
} from "@/lib/sequencingApi";


export default function PolicyRecommenderCard({ group, onApplied }) {
  const [rec, setRec] = useState(null);
  const [computedAt, setComputedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  // Fetch latest cached recommendation on mount — no LLM cost.
  useEffect(() => {
    seqPolicyRecommendationLatest(group.id)
      .then(d => {
        setRec(d.recommendation || null);
        setComputedAt(d.computed_at || null);
      })
      .catch(() => { /* tolerate */ });
  }, [group.id]);

  async function askAi(force = false) {
    setLoading(true);
    try {
      const r = await seqPolicyRecommendation(group.id, force);
      setRec(r);
      setComputedAt(r.generated_at);
      toast.success(force ? "Re-asked AI." : "AI recommendation ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI call failed");
    } finally { setLoading(false); }
  }

  async function apply() {
    if (!rec) return;
    const lines = [
      `policy → ${rec.recommended_policy}`,
      rec.recommended_policy === "hour_delta"
        ? `threshold_h → ${rec.recommended_threshold_h ?? 50}` : null,
      rec.recommended_bandwidth_pct != null
        ? `bandwidth_pct → ${rec.recommended_bandwidth_pct}` : null,
      rec.recommended_restart_delay_s != null
        ? `restart_delay_s → ${rec.recommended_restart_delay_s}` : null,
    ].filter(Boolean);
    if (!window.confirm("Apply the recommended settings?\n\n" + lines.join("\n"))) return;
    setApplying(true);
    try {
      const patch = { lead_rotation_policy: rec.recommended_policy };
      if (rec.recommended_policy === "hour_delta") {
        patch.lead_rotation_threshold_hours = rec.recommended_threshold_h ?? 50;
      }
      if (rec.recommended_bandwidth_pct != null)
        patch.bandwidth_pct = rec.recommended_bandwidth_pct;
      if (rec.recommended_restart_delay_s != null)
        patch.restart_delay_s = rec.recommended_restart_delay_s;
      await seqPatchGroup(group.id, patch);
      toast.success("Applied recommendation.");
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setApplying(false); }
  }

  if (!rec) {
    return (
      <div data-testid="policy-rec-empty"
           className="bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={14} className="text-blue-700" weight="fill" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Unsure which policy fits?
          </h3>
        </div>
        <p className="text-[11px] text-slate-600 leading-snug mb-3">
          Get a Claude-powered recommendation tailored to this group's
          member types, working hours, and the last 7 days of pressure data.
          Includes a suggested bandwidth and restart-delay too.
        </p>
        <button type="button" onClick={() => askAi(false)} disabled={loading}
                data-testid="policy-rec-ask"
                className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <CircleNotch size={11} className="animate-spin" /> : <Sparkle size={11} weight="fill" />}
          Ask AI for recommendation
        </button>
      </div>
    );
  }

  const confColor = rec.confidence >= 75 ? "emerald"
                  : rec.confidence >= 50 ? "amber" : "rose";

  return (
    <div data-testid="policy-rec-card"
         className="bg-white border border-blue-300 rounded-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkle size={14} className="text-blue-700" weight="fill" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
          AI recommendation
        </h3>
        <span data-testid="policy-rec-confidence"
              className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm
                          bg-${confColor}-100 text-${confColor}-700 border border-${confColor}-300`}>
          {rec.confidence}% confidence
        </span>
        {rec.cache_hit && (
          <span title={computedAt ? `Cached from ${new Date(computedAt).toLocaleString()}` : "Cached"}
                className="text-[9px] text-slate-400 uppercase tracking-wider">
            cached
          </span>
        )}
        <button type="button" onClick={() => askAi(true)} disabled={loading}
                data-testid="policy-rec-reask"
                title="Re-ask AI (forces a fresh call)"
                className="ml-auto text-[10px] uppercase font-bold px-2 py-1 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <CircleNotch size={10} className="animate-spin" /> : <ArrowsCounterClockwise size={10} weight="bold" />}
          re-ask
        </button>
      </div>

      {/* Headline recommendation */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-wider text-blue-800 font-bold mb-1">
          Recommended policy
        </div>
        <div className="text-base font-bold text-slate-900 font-mono">
          {rec.recommended_policy}
          {rec.recommended_policy === "hour_delta" && rec.recommended_threshold_h != null && (
            <span className="text-xs text-slate-500"> (threshold: {rec.recommended_threshold_h} h)</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2 text-[11px]">
          {rec.recommended_bandwidth_pct != null && (
            <div data-testid="policy-rec-bandwidth">
              <span className="text-slate-500 uppercase text-[9px] tracking-wider">Bandwidth</span>
              <div className="font-mono font-bold text-slate-900">{rec.recommended_bandwidth_pct}%</div>
            </div>
          )}
          {rec.recommended_restart_delay_s != null && (
            <div data-testid="policy-rec-restart-delay">
              <span className="text-slate-500 uppercase text-[9px] tracking-wider">Restart delay</span>
              <div className="font-mono font-bold text-slate-900">{rec.recommended_restart_delay_s} s</div>
            </div>
          )}
        </div>
      </div>

      <p data-testid="policy-rec-rationale"
         className="text-[11px] text-slate-700 leading-snug">
        {rec.rationale}
      </p>

      {/* Risks */}
      {rec.risks?.length > 0 && (
        <div data-testid="policy-rec-risks"
             className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px]">
          <div className="flex items-center gap-1 mb-1">
            <Warning size={11} weight="fill" className="text-amber-700" />
            <span className="font-bold text-amber-800 uppercase tracking-wider text-[10px]">Risks</span>
          </div>
          <ul className="list-disc pl-4 space-y-0.5 text-slate-700">
            {rec.risks.slice(0, 5).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {/* Compare-all toggle */}
      <button type="button" onClick={() => setShowCompare(!showCompare)}
              data-testid="policy-rec-compare-toggle"
              className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:underline inline-flex items-center gap-1">
        <Question size={10} weight="bold" />
        {showCompare ? "Hide comparison" : "Compare all 3 policies"}
      </button>

      {showCompare && rec.comparison?.length > 0 && (
        <div data-testid="policy-rec-comparison"
             className="grid grid-cols-3 gap-2">
          {rec.comparison.map(c => (
            <div key={c.policy} data-testid={`policy-rec-compare-${c.policy}`}
                  className={`border rounded-sm p-2 text-[10px]
                              ${c.policy === rec.recommended_policy
                                ? "border-blue-400 bg-blue-50"
                                : "border-slate-200 bg-white"}`}>
              <div className="font-bold font-mono text-slate-800 mb-1 flex items-center justify-between">
                <span>{c.policy}</span>
                <span className="text-[9px] text-slate-500">{c.fit_score}/100</span>
              </div>
              {c.pros?.length > 0 && (
                <div className="mb-1">
                  <div className="text-emerald-700 font-bold text-[9px] uppercase tracking-wider">✓ Pros</div>
                  <ul className="list-disc pl-3 space-y-0.5 text-slate-600 leading-snug">
                    {c.pros.slice(0, 3).map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              )}
              {c.cons?.length > 0 && (
                <div>
                  <div className="text-rose-700 font-bold text-[9px] uppercase tracking-wider">✗ Cons</div>
                  <ul className="list-disc pl-3 space-y-0.5 text-slate-600 leading-snug">
                    {c.cons.slice(0, 3).map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={apply} disabled={applying || loading}
              data-testid="policy-rec-apply"
              className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 inline-flex items-center gap-1.5 disabled:opacity-50">
        {applying ? <CircleNotch size={11} className="animate-spin" />
                  : <CheckCircle size={11} weight="fill" />}
        Apply recommendation
      </button>
    </div>
  );
}
